// @flow
import React, { Component, Fragment } from "react";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import AddBoxIcon from "@material-ui/icons/AddBox";
import EmailIcon from "@material-ui/icons/Email";
import EditIcon from "@material-ui/icons/Edit";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import Collapse from "@material-ui/core/Collapse";
import Link from "@material-ui/core/Link";

import {
  GeneralModalForm,
  SingleSwitch,
  MultiLineStrings
} from "imagus-components";
import AddVersionFormRedux from "../../containers/softwares/AddVersionFormRedux";
import BroadcastForm from "../../containers/softwares/BroadcastForm";
import VersionList from "./VersionList";
import GetDownloadLinkForm from "./GetDownloadLinkForm";

type SoftwareItemPropsType = {
  software: { [key: string]: * },
  isAdmin: boolean,
  isBetaUser: boolean,
  downloadFile: Function
};

type SoftwareItemStatesType = {
  openPrev: boolean,
  openBeta: boolean,
  updateModal: boolean,
  addModal: boolean,
  emailModal: boolean,
  getModal: boolean
};

class SoftwareItem extends Component<
  SoftwareItemPropsType,
  SoftwareItemStatesType
> {
  constructor(props: SoftwareItemPropsType) {
    super(props);
    this.state = {
      openPrev: false,
      openBeta: false,
      updateModal: false,
      addModal: false,
      emailModal: false,
      getModal: false
    };
  }

  switchModalStatus =
    (modalName: string): * =>
    (status: boolean): * => {
      this.setState({ [modalName]: status });
    };

  handleSwitchChange =
    (field: string): * =>
    (): * => {
      this.setState({ [field]: !this.state[field] });
    };

  render(): * {
    const software = this.props.software;
    const currentVersion = software.currentVersion;
    return (
      <Paper elevation={4} style={{ padding: "16px", marginBottom: "16px" }}>
        <Typography variant="h6">Installing {software.name}</Typography>
        <Typography variant="subtitle1">
          <ol>
            <li>
              Download Latest Build from here:{" "}
              <Link
                style={{ cursor: "pointer" }}
                onClick={(): * =>
                  this.props.downloadFile(software.id, currentVersion.versionId)
                }
              >
                {software.name + " " + currentVersion.versionId}
              </Link>
            </li>
            <li>Run the {software.name}.</li>
            {software.fileLocation && (
              <li>
                The files will be introduced to the following location:{" "}
                <span style={{ fontStyle: "italic" }}>
                  {software.fileLocation}
                </span>
              </li>
            )}
          </ol>
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
          Version Id:
        </Typography>
        <Typography
          variant="body2"
          className="left-margin-20 bottom-margin-10 top-margin-10"
        >
          {currentVersion.versionId}
          {currentVersion.isCurrent && (
            <span className="green-label left-margin-5">Latest</span>
          )}
          {!currentVersion.isPublic && (
            <span className="red-label left-margin-5">Not Released</span>
          )}
          {currentVersion.isBeta && (
            <span className="blue-label left-margin-5">Beta</span>
          )}
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
          Release Date:
        </Typography>
        <Typography
          variant="body2"
          className="left-margin-20 bottom-margin-10 top-margin-10"
        >
          {currentVersion.releaseDate}
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
          Release Notes:
          {this.props.isAdmin && (
            <Fragment>
              <IconButton
                aria-label="Usage"
                className="left-margin-5"
                onClick={(): * => this.switchModalStatus("updateModal")(true)}
              >
                <EditIcon className="shine-icon" />
              </IconButton>
              <GeneralModalForm
                title={"Edit " + software.name + " " + currentVersion.versionId}
                open={this.state.updateModal}
                handleModalStatus={this.switchModalStatus("updateModal")}
                form={
                  <AddVersionFormRedux
                    onClose={(): * =>
                      this.switchModalStatus("updateModal")(false)
                    }
                    softwareId={software.id}
                    version={currentVersion}
                  />
                }
              />
            </Fragment>
          )}
        </Typography>
        <Typography variant="body2" className="left-margin-20">
          <MultiLineStrings val={currentVersion.releaseNotes} />
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
          <span className="right-margin-10">Previous Releases:</span>
          <SingleSwitch
            value={this.state.openPrev}
            name="openPrev"
            isRequired={false}
            label={["Show", "Hide"]}
            handleChange={this.handleSwitchChange("openPrev")}
          />
          {(this.props.isAdmin || this.props.isBetaUser) && (
            <Fragment>
              <span className="right-margin-10 left-margin-20">
                Beta Versions:
              </span>
              <SingleSwitch
                value={this.state.openBeta}
                name="openBeta"
                isRequired={false}
                label={["Show", "Hide"]}
                handleChange={this.handleSwitchChange("openBeta")}
              />
            </Fragment>
          )}
        </Typography>
        <Collapse in={this.state.openPrev} collapsedHeight="10px">
          <VersionList
            versions={software.versions}
            isAdmin={this.props.isAdmin}
            softwareId={software.id}
            downloadFile={this.props.downloadFile}
          />
        </Collapse>
        {(this.props.isAdmin || this.props.isBetaUser) && (
          <Collapse in={this.state.openBeta} collapsedHeight="10px">
            <VersionList
              versions={software.betaVersions}
              isAdmin={this.props.isAdmin}
              softwareId={software.id}
              downloadFile={this.props.downloadFile}
            />
          </Collapse>
        )}

        {this.props.isAdmin && (
          <Fragment>
            <Button
              type="button"
              variant="contained"
              color="primary"
              className="dark-btn"
              onClick={(): * => this.switchModalStatus("addModal")(true)}
            >
              <AddBoxIcon className="light-icon right-margin-5" />
              Add Version
            </Button>
            <GeneralModalForm
              title={"Add Version for " + software.name}
              open={this.state.addModal}
              handleModalStatus={this.switchModalStatus("addModal")}
              form={
                <AddVersionFormRedux
                  onClose={(): * => this.switchModalStatus("addModal")(false)}
                  softwareId={software.id}
                />
              }
            />
            <Button
              type="button"
              variant="contained"
              color="primary"
              className="dark-btn left-margin-10"
              onClick={(): * => this.switchModalStatus("emailModal")(true)}
            >
              <EmailIcon className="light-icon right-margin-5" />
              Broadcasting
            </Button>
            <GeneralModalForm
              title={"Broadcasting " + software.name}
              open={this.state.emailModal}
              handleModalStatus={this.switchModalStatus("emailModal")}
              maxMd={true}
              form={
                <BroadcastForm
                  onClose={(): * => this.switchModalStatus("emailModal")(false)}
                  softwareId={software.id}
                  currentVersion={currentVersion}
                />
              }
            />
            <Button
              type="button"
              variant="contained"
              color="primary"
              className="dark-btn left-margin-10"
              onClick={(): * => this.switchModalStatus("getModal")(true)}
            >
              <CloudDownloadIcon className="light-icon right-margin-5" />
              Admin Link
            </Button>
            <GeneralModalForm
              title={"Get " + software.name + " Latest Link"}
              open={this.state.getModal}
              handleModalStatus={this.switchModalStatus("getModal")}
              form={
                <GetDownloadLinkForm
                  onClose={(): Function =>
                    this.switchModalStatus("getModal")(false)
                  }
                  downloadFile={this.props.downloadFile}
                  linkList={[
                    {
                      softwareId: software.id,
                      versionId: currentVersion.versionId
                    }
                  ]}
                />
              }
            />
          </Fragment>
        )}
      </Paper>
    );
  }
}

export default SoftwareItem;
